import type { FuseNavItemType } from "@fuse/core/FuseNavigation/types/FuseNavItemType";
import type { ISpaceMenuResponse, ISpaceMenuTipoRestricao } from "@/api/space/dtos/authenticator.dto";

export type EstadoMenuItem = {
	disabled?: boolean;
	restricaoTipo?: ISpaceMenuTipoRestricao;
};

/**
 * Metadados de UI por tipo de restrição (ícone lucide + tooltip).
 * Os únicos tipos existentes são S, C e M — todos bloqueiam (exibe desabilitado).
 */
export const META_RESTRICAO = {
	S: {
		badge: "PRO",
		badgeColor: "border-emerald-800/70 bg-emerald-950/40 text-emerald-300",
		icon: "lucide:lock",
		tooltip: "Recurso sob demanda. Entre em contato com o suporte para adquirir.",
	},
	C: {
		badge: "CONFIG",
		badgeColor: "border-blue-800/70 bg-blue-950/40 text-blue-300",
		icon: "lucide:settings",
		tooltip: "Recurso aguardando configuração. Entre em contato com o suporte.",
	},
	M: {
		badge: "MANUT.",
		badgeColor: "border-red-800/70 bg-red-950/40 text-red-300",
		icon: "lucide:wrench",
		tooltip: "Recurso em manutenção. Indisponível no momento.",
	},
} satisfies Record<
	ISpaceMenuTipoRestricao,
	{
		badge: string;
		badgeColor: string;
		icon: string;
		tooltip: string;
	}
>;

/**
 * Monta um mapa `tag -> estado` (allow-list) a partir da resposta de `menu/listar`.
 *
 * Só entram tags com `status === "A"`. O valor é `{}` (liberado) ou
 * `{ disabled, restricaoTipo }` quando há restrição para o `id_cad_grupo` do usuário.
 * Tags inativas ou ausentes simplesmente não entram no mapa (não aparecem na navbar).
 */
export function montarMapaEstadoMenu(menu: ISpaceMenuResponse, idCadGrupo: number): Map<string, EstadoMenuItem> {
	const mapa = new Map<string, EstadoMenuItem>();

	for (const item of menu ?? []) {
		if (item.status !== "A") continue;

		const bloqueio = item.restricoes?.find((restricao) => restricao.id_cad_grupo === idCadGrupo);

		mapa.set(item.tag, bloqueio ? { disabled: true, restricaoTipo: bloqueio.tipo_restricao } : {});
	}

	return mapa;
}

type OpcoesNavegacao = {
	isSpaceUser: boolean;
	mapaEstado: Map<string, EstadoMenuItem>;
};

/**
 * Aplica permissões na árvore de navegação (allow-list dirigida pelo `menu/listar`):
 * - usuário Space → tudo visível e habilitado;
 * - container (children não-vazio) → aparece apenas se sobrar ao menos um filho visível;
 * - folha → aparece apenas se a `tag` estiver liberada no mapa; restrição vira `disabled` + ícone.
 *
 * Obs.: o `unflattenNavigation` devolve folhas com `children: []`, por isso a detecção de container
 * usa `children?.length` (array não-vazio) — caso contrário toda folha cairia no ramo de container.
 */
export function aplicarPermissoesNavegacao(items: FuseNavItemType[], opcoes: OpcoesNavegacao): FuseNavItemType[] {
	return items.reduce<FuseNavItemType[]>((visiveis, item) => {
		if (opcoes.isSpaceUser) {
			const filhos = item.children?.length ? aplicarPermissoesNavegacao(item.children, opcoes) : undefined;
			visiveis.push({
				...item,
				hidden: false,
				disabled: false,
				restricaoTipo: undefined,
				hasPermission: true,
				...(filhos ? { children: filhos } : {}),
			});
			return visiveis;
		}

		if (item.children?.length) {
			const filhos = aplicarPermissoesNavegacao(item.children, opcoes);
			if (filhos.length > 0) visiveis.push({ ...item, hasPermission: true, children: filhos });
			return visiveis;
		}

		const estado = item.tag ? opcoes.mapaEstado.get(item.tag) : undefined;
		if (!estado) return visiveis;

		visiveis.push({ ...item, ...estado, hasPermission: true });
		return visiveis;
	}, []);
}
